import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SupportChatAppearance,
  SupportConversation,
  SupportVisitor,
  useWembiiSupportCore,
} from "../../../cores/wembiiSupport";
import { useMeCore } from "../../../cores/me";
import { useSocketCore } from "../../../cores/socket";
import { defaultSupportAppearance } from "../../component/support/supportChatTheme";

function dedupeByUserKey<T extends { id: string; userId?: string }>(items: T[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.userId || item.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function dedupeById<T extends { id: string }>(items: T[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}
export { defaultSupportAppearance } from "../../component/support/supportChatTheme";

export function useWembiiAdminSupportScreen(activeTab: string) {
  const { user } = useMeCore();
  const { disconnected } = useSocketCore();
  const {
    config,
    conversations,
    selected,
    messages,
    visitors,
    loadConfig,
    saveConfig,
    uploadConfigAvatar,
    loadConversations,
    loadConversationDetail,
    selectConversation,
    loadMessages,
    loadVisitors,
    subscribeAsAgent,
    sendMessage,
  } = useWembiiSupportCore();

  const [appearance, setAppearance] = useState<SupportChatAppearance>(
    defaultSupportAppearance
  );
  const [conversationDetail, setConversationDetail] =
    useState<SupportConversation | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [detectedVisitor, setDetectedVisitor] = useState<SupportVisitor | null>(
    null
  );
  const saveTimerRef = useRef<number | null>(null);

  const uniqueConversations = useMemo(
    () =>
      dedupeByUserKey(
        dedupeById(conversations).map((conversation) => ({
          ...conversation,
          userId: conversation.visitorId,
        }))
      ),
    [conversations]
  );

  const uniqueVisitors = useMemo(
    () => dedupeByUserKey(visitors),
    [visitors]
  );

  const supportVisitors = useMemo(
    () =>
      detectedVisitor
        ? dedupeByUserKey([...uniqueVisitors, detectedVisitor])
        : uniqueVisitors,
    [uniqueVisitors, detectedVisitor]
  );

  useEffect(() => {
    loadConfig().then((cfg) => {
      if (cfg) setAppearance({ ...defaultSupportAppearance, ...cfg });
    });
  }, [loadConfig]);

  useEffect(() => {
    if (!user?.id || disconnected) return;
    subscribeAsAgent(String(user.id), selected);
  }, [user?.id, selected?.id, selected?.room, subscribeAsAgent, disconnected]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (activeTab === "conversations") {
      loadConversations();
      loadVisitors();
    }
    if (activeTab === "visitors") {
      loadVisitors();
    }
  }, [activeTab, loadConversations, loadVisitors]);

  useEffect(() => {
    if (uniqueConversations.length && !selected) {
      selectConversation(uniqueConversations[0].id);
    }
  }, [uniqueConversations, selected, selectConversation]);

  useEffect(() => {
    if (activeTab !== "conversations" || !selected?.id) {
      setMessagesLoading(false);
      return;
    }

    let cancelled = false;
    const conversationId = selected.id;

    const loadSelectedConversation = async () => {
      setMessagesLoading(true);
      try {
        const [messagesResult, detailResult] = await Promise.all([
          loadMessages(conversationId),
          loadConversationDetail(conversationId),
        ]);
        if (cancelled) return;
        setConversationDetail(detailResult);
        return messagesResult;
      } catch (error: any) {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 404) {
          setConversationDetail(null);
          selectConversation(null);
          loadConversations();
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    loadSelectedConversation();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    selected?.id,
    loadMessages,
    loadConversationDetail,
    selectConversation,
    loadConversations,
  ]);

  useEffect(() => {
    if (!navigator.geolocation || activeTab !== "visitors") return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDetectedVisitor({
          id: "detected-browser-user",
          name: "Tu ubicación",
          company: "Navegador actual",
          wembiiCompanyName: "Sesión local Wembii",
          city: "Ubicación detectada",
          country: "Browser",
          flag: "📍",
          status: "online",
          color: "linear-gradient(135deg,#2563eb,#60a5fa)",
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => undefined,
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 6000 }
    );
  }, [activeTab]);

  const setAppearanceField = useCallback(
    <K extends keyof SupportChatAppearance>(
      field: K,
      value: SupportChatAppearance[K]
    ) => {
      setAppearance((prev) => {
        const next = { ...prev, [field]: value };
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => {
          saveConfig(next);
        }, 600);
        return next;
      });
    },
    [saveConfig]
  );

  const resetAppearance = useCallback(() => {
    setAppearance(defaultSupportAppearance);
    saveConfig(defaultSupportAppearance);
  }, [saveConfig]);

  const uploadAvatar = useCallback(
    async (file: File) => {
      const updated = await uploadConfigAvatar(file);
      setAppearance((prev) => ({ ...prev, ...updated }));
      return updated;
    },
    [uploadConfigAvatar]
  );

  const selectedConversation = conversationDetail ?? selected;

  const sendAgentReply = useCallback(() => {
    const trimmed = replyDraft.trim();
    if (!trimmed || !selected?.id || !user?.id) return;
    sendMessage(selected.id, trimmed, "agent", String(user.id));
    setReplyDraft("");
  }, [replyDraft, selected?.id, sendMessage, user?.id]);

  const openVisitorConversation = useCallback(
    async (visitor: SupportVisitor) => {
      let list = uniqueConversations;
      if (!list.length) {
        const loaded = await loadConversations();
        list = dedupeByUserKey(
          dedupeById(loaded).map((conversation) => ({
            ...conversation,
            userId: conversation.visitorId,
          }))
        );
      }

      const conversation =
        (visitor.conversationId
          ? list.find((item) => item.id === String(visitor.conversationId))
          : undefined) ??
        list.find((item) => item.visitorId === visitor.id) ??
        list.find(
          (item) =>
            item.name === visitor.name &&
            item.wembiiCompanyName === visitor.wembiiCompanyName
        );

      if (!conversation) return false;
      selectConversation(conversation.id);
      return true;
    },
    [uniqueConversations, loadConversations, selectConversation]
  );

  const formatLocalTime = (timezone?: string) => {
    try {
      return new Intl.DateTimeFormat("es", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: timezone || undefined,
      }).format(new Date());
    } catch {
      return new Intl.DateTimeFormat("es", {
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date());
    }
  };

  return {
    appearance,
    conversations: uniqueConversations,
    selectedConversation,
    messages,
    messagesLoading,
    supportVisitors,
    replyDraft,
    setReplyDraft,
    sendAgentReply,
    setAppearanceField,
    resetAppearance,
    uploadAvatar,
    openVisitorConversation,
    selectConversation,
    formatLocalTime,
    config,
  };
}
